import {
  Control,
  Controller,
  FormState,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { useState } from 'react';
import password from 'secure-random-password';
import { OrgUnitModel } from '../../../../../types';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../utils/roles/client-roles';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import SharedButton from '../../../../shared/buttons/shared-button';
import ProfilePicUploadDialog from '../../user-details/profile-pic-upload-dialog';
import ProfilePhotoAvatar from './profile-photo-avatar';
import { sanitizeUserNameInput } from '../../../../utils/username';
import { Messages } from '../../../../utils/message-constants';
import { UserFormValues } from '../user-schema';
import { useUserNameAutoFill } from './use-user-name-auto-fill';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import AutoFixHighOutlined from '@mui/icons-material/AutoFixHighOutlined';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
interface StepSetupUserProps {
  domains: string[];
  control: Control<UserFormValues>;
  formState: FormState<UserFormValues>;
  getValues: UseFormGetValues<UserFormValues>;
  setValue: UseFormSetValue<UserFormValues>;
  clearErrors: UseFormClearErrors<UserFormValues>;
  isUserNameManual: boolean;
  onUserNameManualChange: (isManual: boolean) => void;
  canSeePasswords: boolean;
  gPanelRoles: string[];
  orgUnits: OrgUnitModel[];
  userRoles: string[];
  directoryRoles: string[];
  profilePhoto: File | null;
  onProfilePhotoChange: (file: File | null) => void;
}

export default function StepSetupUser({
  domains,
  control,
  formState: { errors, isValidating },
  getValues,
  setValue,
  clearErrors,
  isUserNameManual,
  onUserNameManualChange,
  canSeePasswords,
  gPanelRoles,
  orgUnits,
  userRoles,
  directoryRoles,
  profilePhoto,
  onProfilePhotoChange,
}: StepSetupUserProps) {
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const { status, applyUserName, ...nameHandlers } = useUserNameAutoFill({
    control,
    getValues,
    setValue,
    clearErrors,
    isUserNameManual,
    onUserNameManualChange,
  });

  function getUserNameHelper() {
    // Checked before form errors so the "Use …" link and the suggestion survive
    // Continue, which reports the same taken name as a plain message.
    if (status.kind === 'taken') {
      const { suggestion } = status;
      return {
        error: true,
        text: suggestion ? (
          <>
            {Messages.UserName.Taken}{' '}
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => applyUserName(suggestion)}
            >
              {Messages.UserName.UseSuggestion(suggestion)}
            </Link>
          </>
        ) : (
          Messages.UserName.Taken
        ),
      };
    }
    if (status.kind === 'suggested') {
      return {
        error: false,
        text: status.suggestion
          ? Messages.UserName.Suggested(status.takenUserName, status.suggestion)
          : Messages.UserName.TakenNoSuggestion(status.takenUserName),
      };
    }
    if (errors.userName?.message) {
      return { error: true, text: errors.userName.message };
    }
    return { error: false, text: ' ' };
  }
  const userNameHelper = getUserNameHelper();

  const canChangePhoto = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.ChangeProfilePicture.value
  );
  const photoPermissionTooltip = canChangePhoto
    ? ''
    : Messages.Errors.Permission.Acting(
        DirectoryRoles.ChangeProfilePicture.name
      );

  return (
    <Box>
      <FormSectionHeader sx={{ mb: 2 }}>Basic Information</FormSectionHeader>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 3 }}>
        <ProfilePhotoAvatar file={profilePhoto} />
        <Tooltip arrow title={photoPermissionTooltip}>
          <span>
            <SharedButton
              size="small"
              startIcon={<FileUploadOutlined />}
              disabled={!canChangePhoto}
              onClick={() => setOpenPhotoDialog(true)}
            >
              Upload Photo
            </SharedButton>
          </span>
        </Tooltip>
        {(profilePhoto || !canChangePhoto) && (
          <Tooltip arrow title={photoPermissionTooltip}>
            <span>
              <SharedButton
                size="small"
                variant="secondary"
                disabled={!canChangePhoto}
                onClick={() => onProfilePhotoChange(null)}
              >
                Remove Photo
              </SharedButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <ProfilePicUploadDialog
        open={openPhotoDialog}
        handleClose={() => setOpenPhotoDialog(false)}
        title="Upload Profile Picture"
        onSave={(file) => {
          onProfilePhotoChange(file);
          setOpenPhotoDialog(false);
        }}
      />
      <Stack flexDirection="row" gap={5} sx={{ mb: 1 }}>
        <Controller
          name="name.givenName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(event) => {
                field.onChange(event);
                nameHandlers.onGivenNameChange(event.target.value);
              }}
              autoComplete="off"
              required
              label="First Name"
              error={!!errors.name?.givenName}
              helperText={errors?.name?.givenName?.message || ' '}
              sx={{ minWidth: 400 }}
            />
          )}
        />
        <Controller
          name="name.familyName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(event) => {
                field.onChange(event);
                nameHandlers.onFamilyNameChange(event.target.value);
              }}
              required
              autoComplete="off"
              label="Last Name"
              error={!!errors.name?.familyName}
              helperText={errors?.name?.familyName?.message || ' '}
              sx={{ minWidth: 400 }}
            />
          )}
        />
      </Stack>
      <Stack flexDirection="row" gap={5} sx={{ mb: 1 }}>
        <Controller
          name="userName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(event) => {
                field.onChange(sanitizeUserNameInput(event.target.value));
                clearErrors('userName');
                onUserNameManualChange(true);
              }}
              required
              autoComplete="off"
              label="User Name"
              placeholder="first.last"
              error={userNameHelper.error}
              helperText={userNameHelper.text}
              sx={{ minWidth: 400 }}
              slotProps={{
                input: {
                  endAdornment: isValidating ? (
                    <CircularProgress size={20} />
                  ) : null,
                },
              }}
            />
          )}
        />

        <Controller
          name="domain"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              inputProps={{
                id: 'domain',
              }}
              InputLabelProps={{
                htmlFor: 'domain',
              }}
              select
              required
              label="Domain"
              sx={{ minWidth: 400 }}
            >
              {domains.map((domainOption) => (
                <MenuItem key={domainOption} value={domainOption}>
                  {domainOption}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Stack>
      <Stack direction="row" spacing={5} sx={{ mb: 2 }}>
        <Stack flexDirection="row" gap={1} alignItems="baseline">
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                onCopy={(event) => {
                  if (!canSeePasswords) event.preventDefault();
                }}
                onCut={(event) => {
                  if (!canSeePasswords) event.preventDefault();
                }}
                type={!canSeePasswords ? 'password' : undefined}
                label="Password"
                error={!!errors.password}
                helperText={errors?.password?.message || ' '}
                sx={{ width: 400 }}
              />
            )}
          />
          <Tooltip title="Create Password" arrow>
            <IconButton
              onClick={() => {
                const generatedPassword = password.randomPassword({
                  length: 16,
                  characters: [
                    password.lower,
                    password.upper,
                    password.digits,
                    password.symbols,
                  ],
                });

                setValue('password', generatedPassword, {
                  shouldDirty: true,
                });
                setValue('confirmPassword', generatedPassword, {
                  shouldDirty: true,
                });
              }}
              sx={{ position: 'relative', top: 6 }}
            >
              <AutoFixHighOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={5} sx={{ mb: 1 }}>
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              onCopy={(event) => {
                if (!canSeePasswords) event.preventDefault();
              }}
              onCut={(event) => {
                if (!canSeePasswords) event.preventDefault();
              }}
              type={!canSeePasswords ? 'password' : undefined}
              label="Confirm Password"
              error={!!errors.confirmPassword}
              helperText={errors?.confirmPassword?.message || ' '}
              sx={{ width: 400, mt: 1 }}
            />
          )}
        />
      </Stack>
      <Stack direction="row" spacing={5} sx={{ mb: 2 }}>
        <Controller
          name="changePasswordAtNextLogin"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => props.onChange(e.target.checked)}
                />
              }
              label="Require user to change password at next sign-in."
            />
          )}
        />
      </Stack>

      <FormSectionHeader sx={{ mt: 3, mb: 2 }}>OU and Roles</FormSectionHeader>
      <Stack direction="column" spacing={3} sx={{ mb: 2 }}>
        <Controller
          name="orgUnitPath"
          control={control}
          render={({ field }) => (
            <Autocomplete
              id="org-unit-autocomplete"
              value={orgUnits.find((orgUnit) => {
                if (!field.value) {
                  return orgUnit.orgUnitPath === '/';
                } else {
                  return orgUnit.orgUnitPath === field.value;
                }
              })}
              onChange={(_event, newValue) => {
                setValue('orgUnitPath', newValue?.orgUnitPath || '/', {
                  shouldDirty: true,
                });
              }}
              options={orgUnits.sort((a, b) => {
                if (a.orgUnitPath < b.orgUnitPath) return -1;
                if (a.orgUnitPath > b.orgUnitPath) return 1;
                return 0;
              })}
              getOptionLabel={(option) => option.orgUnitPath}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization Unit"
                  sx={{ width: 400 }}
                />
              )}
            />
          )}
        />
        <Controller
          name="cPanelRole"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              inputProps={{
                id: 'cpanel-role',
              }}
              InputLabelProps={{
                htmlFor: 'cpanel-role',
              }}
              select
              required
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.ChangeCPanelRole.value
                )
              }
              label="cPanel Role"
              sx={{ width: 400 }}
            >
              <MenuItem value={'admin'}>Admin</MenuItem>
              <MenuItem value={'user'}>User</MenuItem>
            </TextField>
          )}
        />

        <Controller
          name="gPanelRole"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              inputProps={{
                id: 'gpanel-role',
              }}
              InputLabelProps={{
                htmlFor: 'gpanel-role',
              }}
              required
              select
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.ChangeGPanelRole.value
                )
              }
              label="gPanel Role"
              sx={{ maxWidth: 400 }}
            >
              {gPanelRoles?.length ? (
                gPanelRoles?.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={''}>No Roles Found</MenuItem>
              )}
            </TextField>
          )}
        />
        <Controller
          name="includeInGlobalAddressList"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => props.onChange(e.target.checked)}
                />
              }
              label={`Automatically share ${
                getValues('name.givenName').length
                  ? getValues('name.givenName')
                  : 'user'
              }'s contact information.`}
              sx={{ width: 'fit-content' }}
            />
          )}
        />
      </Stack>
    </Box>
  );
}
